import Heading, { hasHeading } from 'molecules/Heading';
import Icon from 'molecules/Icon';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import useNavatticFallback from 'components/IFrame/useNavatticFallback';

import type { IFrameConfigProps } from 'components/IFrame/config';
import type { FC, ReactNode } from 'react';

const aspectRatioWrapperClass: Record<string, string> = {
  navattic: 'relative h-0 w-full pb-[calc(56.25%+41px)]',
  '16:9': 'relative h-0 w-full pb-[56.25%]',
  '4:3': 'relative h-0 w-full pb-[75%]',
};

const IFrame: FC<IFrameConfigProps> = props => {
  const {
    sectionProps,
    rest: { iframe, aspectRatio, hideOnMobile, ...headingProps },
  } = splitSectionProps(props);

  const showHeading = hasHeading(headingProps);
  const src = iframe?.value?.data?.src;
  const isNavattic = typeof src === 'string' && src.includes('navattic.com');
  const { showFallback, handleIframeLoad } = useNavatticFallback(src, isNavattic);

  const renderSection = (children: ReactNode) => {
    const section = <Section {...sectionProps}>{children}</Section>;

    return hideOnMobile ? <div className="hidden sm:block">{section}</div> : section;
  };

  if (!iframe?.value?.data) {
    return renderSection(showHeading && <Heading {...headingProps} />);
  }

  const wrapperClass = aspectRatio ? aspectRatioWrapperClass[aspectRatio] : undefined;
  const title = iframe.value.data.title || 'Embedded content';

  return renderSection(
    <div className="flex flex-col gap-10">
      {showHeading && <Heading {...headingProps} />}
      <div className={wrapperClass ?? 'relative'}>
        <iframe
          title={title}
          src={src}
          className={wrapperClass ? 'absolute inset-0 size-full' : 'h-screen w-full'}
          loading="lazy"
          allow={wrapperClass ? 'fullscreen' : undefined}
          onLoad={isNavattic ? handleIframeLoad : undefined}
        />
        {isNavattic && (
          <div
            aria-live="polite"
            className={`absolute inset-0 flex items-center justify-center overflow-hidden ${
              showFallback ? 'pointer-events-auto' : 'pointer-events-none'
            }`}
          >
            {showFallback && (
              <>
                <div className="absolute inset-0 bg-gray-900" />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-[url('/images/demo-alert.png')] bg-center bg-no-repeat blur-sm brightness-125 contrast-110 bg-[length:100%]"
                />
                <div className="relative z-10 flex max-w-sm flex-col items-center gap-3 rounded-lg bg-white p-6 text-center shadow-lg">
                  <Icon icon="warning" size={24} iconColor="text-gray-500" />
                  <p className="text-sm leading-6 text-gray-600">
                    Not seeing the walkthrough? Privacy tools sometimes block it. Pause your ad blocker or privacy
                    extension, then refresh.
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>,
  );
};

export default IFrame;
